import { useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StarRatingProps {
  value: number;
  onChange: (value: number) => void;
  /** Accessible labels per star, index 0..4 → 1..5 stars. */
  labels?: string[];
  disabled?: boolean;
  size?: number;
}

/**
 * Controlled 1–5 star rating input. Keyboard- and screen-reader-accessible:
 * each star is a real <button> with an aria-label and aria-pressed state.
 */
export function StarRating({
  value,
  onChange,
  labels,
  disabled = false,
  size = 32,
}: StarRatingProps) {
  const [hover, setHover] = useState(0);

  return (
    <div className="flex items-center gap-[8px]" role="radiogroup">
      {[1, 2, 3, 4, 5].map((star) => {
        const active = (hover || value) >= star;
        return (
          <button
            key={star}
            type="button"
            disabled={disabled}
            role="radio"
            aria-checked={value === star}
            aria-label={labels?.[star - 1] || `${star}`}
            onClick={() => !disabled && onChange(star)}
            onMouseEnter={() => !disabled && setHover(star)}
            onMouseLeave={() => setHover(0)}
            className={cn(
              'rounded-[8px] p-[2px] transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--main-brand,#2197f1)]',
              !disabled && 'hover:scale-110 cursor-pointer',
              disabled && 'cursor-default',
            )}
          >
            <Star
              size={size}
              className={cn(
                'transition-colors',
                active
                  ? 'fill-amber-400 text-amber-400'
                  : 'fill-transparent text-gray-300',
              )}
            />
          </button>
        );
      })}
    </div>
  );
}

export default StarRating;
